import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OrganizationSettingTypeEnum } from '@libs/types/src/organization/enum/organization-setting-type.enum';
import {
    SporitelnaBankAccountObject,
} from '@libs/types/src/chcrm/object-type/sporitelna/sporitelna-bank-account.object';
import { v4 as createUuid } from 'uuid';
import {
    OrganizationSettingsRaiffeisenType,
} from '@libs/types/src/organization/types/organization-settings/organization-settings-raiffeisen.type';
import { OrganizationTypeEnum } from '../../../../../../../../../types/src/mado/enum/organization-type.enum';

function darujmeForm(fb: FormBuilder): FormGroup {
    return fb.group({
        organizationId: fb.control<null | number>(null, Validators.required),
        apiId: fb.control('', Validators.required),
        apiSecret: fb.control('', Validators.required),
        fallbackCollectionId: fb.control('', Validators.required),
        defaultDonationForm: fb.control(''),
    });
}

export function createSporitelnaAccountToFormForm(fb: FormBuilder, data?: any): FormGroup {
    return fb.group({
        iban: fb.control(data?.iban),
        donationForm: fb.control(data?.donationForm),
    });
}

export function sporitelnaForm(fb: FormBuilder, data: any, bankAccounts?: SporitelnaBankAccountObject[]): FormGroup {
    bankAccounts = Array.isArray(bankAccounts) ? bankAccounts : [];
    const accountToForm = Array.isArray(data?.accountToForm) ? data.accountToForm : [];
    return fb.group({
        refreshToken: fb.control('', Validators.required),
        fallbackCollectionId: fb.control('', Validators.required),
        bankMapping: bankAccounts ? fb.group({
            ...bankAccounts.reduce((acc, it) => {
                acc[it.identification.iban] = fb.control('');
                return acc;
            }, {} as Record<string, unknown>),
        }) :
            fb.group({
                ...Object.keys(data?.bankMapping || {}).reduce((acc, it) => {
                    acc[it] = fb.control('');
                    return acc;
                }, {} as Record<string, unknown>),
            })
        ,
        defaultDonationFormForCashTransfer: fb.control<string | null>(null),
        defaultDonationForm: fb.control(''),
        accountToForm: fb.array(accountToForm.map((it: any) => createSporitelnaAccountToFormForm(fb, it))),
    });
}

export function createRaiffeisenAccountToFormForm(fb: FormBuilder, data?: any): FormGroup {
    return fb.group({
        iban: fb.control(data?.iban),
        donationForm: fb.control(data?.donationForm),
    });
}

export function raiffeisenForm(fb: FormBuilder, data: any): FormGroup {
    const accounts = (data as OrganizationSettingsRaiffeisenType).accounts || [];
    const accountToForm = Array.isArray(data?.accountToForm) ? data.accountToForm : [];

    return fb.group({
        fallbackCollectionId: fb.control('', Validators.required),
        bankMapping: fb.group({
            ...accounts.reduce((acc, it) => {
                acc[it.iban] = fb.control('');
                return acc;
            }, {} as Record<string, unknown>),
        }),
        defaultDonationForm: fb.control(''),
        accountToForm: fb.array(accountToForm.map((it: any) => createRaiffeisenAccountToFormForm(fb, it))),
        clientId: fb.control(''),
        certPassword: fb.control(''),
        certFilepath: fb.control(''),
    });
}

export function createCollectionCategoryForm(fb: FormBuilder, data?: any): FormGroup {
    return fb.group({
        id: fb.control(data?.id ?? createUuid()),
        name: fb.control(data?.name ?? ''),
        threshold: fb.control((data?.threshold ?? '')),
        exclusive: fb.control(data?.exclusive ?? true),
        priority: fb.control(data?.priority ?? 99),
    });
}

export function collectionCategoriesForm(fb: FormBuilder, data: any): FormGroup {
    return fb.group({
        categories: fb.array((data.categories || []).map((it: any) => createCollectionCategoryForm(fb, it))),
    });
}

export function createDonationFormForm(fb: FormBuilder, data?: any): FormGroup {
    return fb.group({
        id: fb.control(data?.id ?? createUuid()),
        name: fb.control(data?.name ?? ''),
        isDonation: fb.control(data?.isDonation ?? true),
    });
}

export function donationFormsForm(fb: FormBuilder, data: any): FormGroup {
    return fb.group({
        forms: fb.array((data.forms || []).map((it: any) => createDonationFormForm(fb, it))),
        defaultGatewayForm: fb.control<string | null>(null),
    });
}

export function passwordResetForm(fb: FormBuilder): FormGroup {
    return fb.group({
        expireInMonths: fb.control(0, Validators.required),
        applyTo2Fa: fb.control(false),
    });
}

export function systemSettingsForm(fb: FormBuilder, data: any): FormGroup {
    return fb.group({
        passwordReset: passwordResetForm(fb),

        topbarVideocall: fb.control<boolean>(data?.topbarVideocall ?? false),
        topbarFullscreen: fb.control<boolean>(data?.topbarFullscreen ?? false),
        topbarSearch: fb.control<boolean>(data?.topbarSearch ?? false),
        topbarHelp: fb.control<boolean>(data?.topbarHelp ?? false),

        topbarUserEmail: fb.control<boolean>(data?.topbarUserEmail ?? false),
        topbarUserOffice: fb.control<boolean>(data?.topbarUserOffice ?? false),
        topbarUserReports: fb.control<boolean>(data?.topbarUserReports ?? false),
        topbarUserReadMessages: fb.control<boolean>(data?.topbarUserReadMessages ?? false),

    });
}

export function emailingSettingsForm(fb: FormBuilder, data: any): FormGroup {
    return fb.group({
        emailAddress: fb.control(data?.emailAddress ?? data?.email ?? ''),
        replyToAddress: fb.control(data?.replyToAddress ?? ''),
        postmarkToken: fb.control(data?.postmarkToken ?? ''),
    });
}

export function software602SigningForm(fb: FormBuilder, data: any): FormGroup {
    return fb.group({
        baseUrl: fb.control(data?.baseUrl ?? 'https://testsofa.602.cz', Validators.required),
        agendaIdent: fb.control(data?.agendaIdent ?? 'sys_agenda_signpoint_sdk', Validators.required),
        messageIdent: fb.control(data?.messageIdent ?? 'sys_start_template', Validators.required),
    });
}

export function madoSettingsForm(fb: FormBuilder, data: any): FormGroup {
    return fb.group({
        icDph: fb.control<boolean>(Boolean(data?.icDph ?? false)),
        displayedName: fb.control<string | null>(data?.displayedName ?? null, Validators.required),
        organizationType: fb.control<string>(data?.organizationType ?? OrganizationTypeEnum.NONPROFIT, Validators.required),
    });
}

export function madoSettingsTransactionsForm(fb: FormBuilder, data: any): FormGroup {
    return fb.group({
        unpostedTransactionsAccountId: fb.control(data?.unpostedTransactionsAccountId ?? null),
        gainExchangeAccountId: fb.control(data?.gainExchangeAccountId ?? null),
        lossExchangeAccountId: fb.control(data?.lossExchangeAccountId ?? null),
    });
}

export function madoCreditAgreementsForm(fb: FormBuilder, data: any): FormGroup {
    return fb.group({
        incomeInterestAccount: fb.control<string>(data?.incomeInterestAccount),
        incomePenaltyAccount: fb.control<string>(data?.incomePenaltyAccount),
        receivableAccount: fb.control<string>(data?.receivableAccount),
        expenceInterestAccount: fb.control<string>(data?.expenceInterestAccount),
        expencePenaltyAccount: fb.control<string>(data?.expencePenaltyAccount),
        payableAccount: fb.control<string>(data?.payableAccount),
    });
}

export function prepareForm(settingsKey: string, fb: FormBuilder, data: object): FormGroup {
    switch (settingsKey) {
        case OrganizationSettingTypeEnum.DARUJME:
            return darujmeForm(fb);
        case OrganizationSettingTypeEnum.SPORITELNA:
            return sporitelnaForm(fb, data);
        case OrganizationSettingTypeEnum.RAIFFEISEN:
            return raiffeisenForm(fb, data);
        case OrganizationSettingTypeEnum.COLLECTION_CATEGORY:
            return collectionCategoriesForm(fb, data);
        case OrganizationSettingTypeEnum.DONATION_FORM:
            return donationFormsForm(fb, data);
        case OrganizationSettingTypeEnum.PASSWORD_RESET:
            return passwordResetForm(fb);
        case OrganizationSettingTypeEnum.SYSTEM_SETTINGS:
            return systemSettingsForm(fb, data);
        case OrganizationSettingTypeEnum.EMAILING_SETTINGS:
            return emailingSettingsForm(fb, data);
        case OrganizationSettingTypeEnum.SOFTWARE602_SIGNING:
            return software602SigningForm(fb, data);
        case OrganizationSettingTypeEnum.MADO:
            return madoSettingsForm(fb, data);
        case OrganizationSettingTypeEnum.MADO_SETTINGS:
            return madoSettingsTransactionsForm(fb, data);
        case OrganizationSettingTypeEnum.MADO_CREDIT_AGREEMENTS:
            return madoCreditAgreementsForm(fb, data);
    }

    return fb.group({});
}
